import { useEffect, useMemo, useState } from "react";
import { useMediaQuery } from "react-responsive";

// This hook returns an object with two properties: `isDark` and `setIsDark`.
//  `isDark` is a boolean that indicates whether the user prefers a dark or light color scheme.
//  `setIsDark` is a function that can be used to set the preferred color scheme.

//  If the user has a preferred color scheme set in their browser, this hook will return the
//  preferred color scheme. Otherwise, it will return the value of the `colorScheme` item in
//  localStorage, or false if no value is set.

//  The hook also sets the `dark` class on the document body if the user prefers a dark color
//  scheme.
export const useColorScheme = () => {
  const systemPrefersDark = useMediaQuery({
    query: "(prefers-color-scheme: dark)",
  });

  const [isDark, setIsDark] = useState(() => {
    // Retrieve the color scheme from localStorage
    const savedScheme = localStorage.getItem("colorScheme");
    return savedScheme !== null ? JSON.parse(savedScheme) : undefined;
  });

  const value = useMemo(
    () => (isDark === undefined ? !!systemPrefersDark : isDark),
    [isDark, systemPrefersDark]
  );

  useEffect(() => {
    // Persist the color scheme to localStorage
    localStorage.setItem("Theme", JSON.stringify(isDark));
  }, [isDark]);

  useEffect(() => {
    if (value) {
      document.body.classList.add("dark");
    } else {
      document.body.classList.remove("dark");
    }
  }, [value]);

  return {
    isDark: value,
    setIsDark,
  };
};
